import React, { Fragment, useCallback, useEffect, useState } from "react";
import "./index.css";
import {
  Button,
  Card,
  Col,
  Input,
  List,
  Row,
  Typography,
  Form,
  Spin,
  Divider,
} from "antd";
import { isMobile } from "react-device-detect";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import { CartItem } from "../../../domain";
import { useQuery } from "../../hooks";
import { Link, Redirect, useHistory } from "react-router-dom";
import {
  applyOff,
  getPaymentLink,
  loadShipping,
  resetShippingState,
} from "../../../application/shipping-slice";
import { loadCartItems } from "../../../application/cart-slice";
import useWindowDimensions from "../../hooks/useWindowDimensions";

export default function Shipping() {
  const query = useQuery();
  const { height } = useWindowDimensions();
  const dispatch = useAppDispatch();
  const [form] = Form.useForm();
  const {
    loading,
    paymentLinkLoading,
    address,
    deliveryInfo,
    errorCode,
    isAddressValid,
    applyOffLoading,
    discountOffAmount,
    discountOffType,
  } = useAppSelector((state) => state.shipping);
  const { itemsLoading, items } = useAppSelector((state) => state.cart);
  const addressId = query.get("address");
  const [discountCode, setDiscountCode] = useState<string>();

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    if (addressId !== null) {
      stableDispatch(loadCartItems());
      stableDispatch(loadShipping(addressId));
    }
  }, [stableDispatch, addressId]);

  const calculateTotalItemsPrice = (cartItems: CartItem[]) => {
    let sum = 0;
    cartItems.forEach((item) => {
      const attribute = item.product.attributes.find(
        (attribute) => attribute.id.toString() === item.attributeId
      );
      sum +=
        (attribute?.hasOff() ? attribute!.offPrice : attribute!.price) *
        item.count;
    });
    return sum;
  };

  const isDeliveryOffInvalid = (): boolean => {
    if (deliveryInfo === undefined) return false;
    let deliveryPrice = deliveryInfo.deliveryPrice;
    const totalItemsPrice = calculateTotalItemsPrice(items);
    if (totalItemsPrice > deliveryInfo.freeDeliveryMinPrice) deliveryPrice = 0;
    if (discountOffAmount !== 0) {
      if (discountOffType === "delivery" && deliveryPrice === 0) return true;
    }
    return false;
  };

  const calculateTotalPrice = () => {
    if (deliveryInfo === undefined) return "";
    let deliveryPrice = deliveryInfo.deliveryPrice;
    let totalItemsPrice = calculateTotalItemsPrice(items);
    if (totalItemsPrice > deliveryInfo.freeDeliveryMinPrice) deliveryPrice = 0;
    if (discountOffAmount !== 0) {
      if (discountOffType === "product") {
        totalItemsPrice = totalItemsPrice - discountOffAmount;
      } else {
        if (deliveryPrice !== 0)
          deliveryPrice = deliveryPrice - discountOffAmount;
      }
    }
    return (deliveryPrice + totalItemsPrice).toLocaleString() + " تومان";
  };

  const calculateOffAmount = (cartItems: CartItem[]) => {
    let sum = 0;
    cartItems.forEach((item) => {
      const attribute = item.product.attributes.find(
        (attribute) => attribute.id.toString() === item.attributeId
      );
      if (attribute?.hasOff())
        sum += (attribute!.price - attribute!.offPrice) * item.count;
    });
    return sum;
  };

  const calculateTotalPriceWithoutOff = (cartItems: CartItem[]) => {
    let sum = 0;
    cartItems.forEach((item) => {
      sum +=
        item.product.attributes.find(
          (attribute) => attribute.id.toString() === item.attributeId
        )!.price * item.count;
    });
    return sum;
  };

  const calculateOffPercent = (cartItems: CartItem[]) => {
    return Math.ceil(
      (calculateOffAmount(items) / calculateTotalPriceWithoutOff(items)) * 100
    );
  };

  const renderItem = (item: CartItem, index: number) => {
    const selectedAttribute = item.product.attributes.find(
      (attribute) => attribute.id === item.attributeId
    );
    return (
      <List.Item className="cart-item">
        <Col>
          <Row>
            <Link
              to={`/product/${item.productId}/${encodeURIComponent(
                item.product.title
              )}`}
            >
              <img
                src={item.product.images[0]}
                alt={item.product.title}
                width={100}
              />
            </Link>
            <div className="cart-item-info">
              <Typography.Title level={5}>
                {item.product.title}
              </Typography.Title>
              <Typography.Text>
                سایز{" "}
                <span className="cart-item-size">
                  {selectedAttribute!.size}
                </span>{" "}
                به رنگ{" "}
                <span className="cart-item-color">
                  {selectedAttribute!.color.title}
                </span>
              </Typography.Text>
              <Row align="middle" className="cart-item-quantity-selector">
                <div>
                  {selectedAttribute?.hasOff() ? (
                    <Row justify="end">
                      <Typography.Text className="cart-item-off-price">
                        {(selectedAttribute!.price * item.count).toLocaleString(
                          "fa"
                        )}{" "}
                        تومان
                      </Typography.Text>
                    </Row>
                  ) : (
                    <Fragment />
                  )}
                  <Row justify="end">
                    <Typography.Text>
                      {(
                        (selectedAttribute!.hasOff()
                          ? selectedAttribute!.offPrice
                          : selectedAttribute!.price) * item.count
                      ).toLocaleString("fa")}{" "}
                      تومان
                    </Typography.Text>
                  </Row>
                </div>
              </Row>
            </div>
          </Row>
        </Col>
      </List.Item>
    );
  };

  const submit = () => {
    if (addressId !== null)
      dispatch(
        getPaymentLink(
          addressId,
          items,
          discountOffAmount === 0 ? undefined : discountCode
        )
      );
  };

  const history = useHistory();

  const stablePush = useCallback(history.push, []);
  useEffect(() => {
    if (errorCode === "invalidCart") {
      stablePush("/cart?invalid=true");
      stableDispatch(resetShippingState());
    }
  }, [stableDispatch, errorCode, stablePush]);

  if (isAddressValid === false) {
    dispatch(resetShippingState());
    return <Redirect to="/select-address" />;
  }

  if (itemsLoading === "succeeded" && items.length === 0) {
    return <Redirect to="/cart" />;
  }

  const isLoading =
    loading === "pending" ||
    loading === "idle" ||
    itemsLoading === "idle" ||
    itemsLoading === "pending";
  if (isLoading) {
    return (
      <div style={{ height: (height * 3) / 4 }}>
        <Spin
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            WebkitTransform: "translate(-50%, -55%)",
            transform: "translate(-50%, -55%)",
          }}
        />
      </div>
    );
  }
  return (
    <div>
      <Col lg={17} md={16}>
        <Card
          className={isMobile ? "cart-card-mobile" : "cart-card-desktop"}
          bodyStyle={{ padding: 0 }}
        >
          <div className="cart-title">آدرس تحویل</div>
          <Col style={{ padding: 16 }}>
            <div className="addrees-item-title">{`${address?.state}، ${address?.city}`}</div>
            <div className="addrees-item-detail">{address?.address}</div>
            {address?.zipCode === "" ? (
              <Fragment />
            ) : (
              <div className="addrees-item-detail">{address?.zipCode}</div>
            )}
          </Col>
        </Card>
        <Card
          className={isMobile ? "cart-card-mobile" : "cart-card-desktop"}
          bodyStyle={{ padding: 0 }}
        >
          <div className="cart-title">محصولات</div>
          <List
            dataSource={items}
            loading={isLoading}
            renderItem={renderItem}
          />
        </Card>
      </Col>

      <Col lg={7} md={8}>
        <Card
          className={
            isMobile
              ? "purchase-info-card-mobile"
              : "purchase-info-card-desktop"
          }
          bodyStyle={{ padding: 0 }}
        >
          <Row justify="space-between" className="prices-box-text">
            <Typography.Text>قیمت کالاها</Typography.Text>
            <Typography.Text>
              {calculateTotalPriceWithoutOff(items).toLocaleString()} تومان
            </Typography.Text>
          </Row>
          {calculateOffAmount(items) === 0 ? (
            <Fragment />
          ) : (
            <Row justify="space-between" className="prices-box-text">
              <Typography.Text>میزان تخفیف</Typography.Text>
              <Typography.Text>
                ({`${calculateOffPercent(items)}%`}){" "}
                {calculateOffAmount(items).toLocaleString()} تومان
              </Typography.Text>
            </Row>
          )}
          <Row justify="space-between" className="prices-box-text">
            <Typography.Text>هزینه ارسال</Typography.Text>
            <Typography.Text>
              {deliveryInfo &&
              calculateTotalItemsPrice(items) >
                deliveryInfo?.freeDeliveryMinPrice
                ? "رایگان"
                : deliveryInfo?.deliveryPrice.toLocaleString() + " تومان"}
            </Typography.Text>
          </Row>
          <Divider style={{ margin: 0 }} />
          {isDeliveryOffInvalid() === false && discountOffAmount !== 0 ? (
            <Row justify="space-between" className="prices-box-text">
              <Typography.Text className="off-amount">کد تخفیف</Typography.Text>
              <Typography.Text className="off-amount">
                {discountOffAmount.toLocaleString() + " تومان"}
              </Typography.Text>
            </Row>
          ) : (
            <Fragment />
          )}
          <Row className="total-price-text" justify="space-between">
            <Typography.Text>مبلغ قابل پرداخت</Typography.Text>
            <Typography.Text>{calculateTotalPrice()}</Typography.Text>
          </Row>
          <div style={{ margin: 8 }}>
            <Form form={form} fields={[{ name: "discount-code" }]}>
              <Row justify="space-between">
                <Form.Item
                  name="discount-code"
                  style={{ marginBottom: 0, width: "calc(100% - 100px)" }}
                  validateStatus={
                    errorCode === "expiredDiscountCode" ||
                    errorCode === "wrongDiscountCode" ||
                    errorCode === "codeAlreadyUsed" ||
                    isDeliveryOffInvalid()
                      ? "error"
                      : undefined
                  }
                  help={
                    errorCode === "expiredDiscountCode"
                      ? "تاریخ انقضای کد تخفیف گذشته است."
                      : errorCode === "wrongDiscountCode" ||
                        isDeliveryOffInvalid()
                      ? "کد تخفیف نادرست است."
                      : errorCode === "codeAlreadyUsed"
                      ? "کد تخفیف قبلا استفاده شده است."
                      : undefined
                  }
                >
                  <Input
                    name="discount-code"
                    placeholder="کد تخفیف"
                    onChange={(e) => setDiscountCode(e.target.value)}
                  />
                </Form.Item>
                <Button
                  type="ghost"
                  style={{ width: 90 }}
                  loading={applyOffLoading === "pending"}
                  disabled={discountCode === undefined || discountCode === ""}
                  onClick={() => {
                    if (discountCode !== undefined)
                      dispatch(applyOff(discountCode));
                  }}
                >
                  ثبت
                </Button>
              </Row>
            </Form>
          </div>
          <div className="submit-purchase-button">
            <Button
              type="primary"
              size="large"
              block
              loading={paymentLinkLoading === "pending"}
              onClick={submit}
            >
              پرداخت و ثبت نهایی سفارش
            </Button>
          </div>
        </Card>
      </Col>
    </div>
  );
}
